/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Form component for creating new bike bus rides. Handles input validation,
stops management, and submission to the bike bus service.
*/

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { bikeBusService, BikeBusStop } from "@/api/bikeBusService";

interface BikeBusFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormStop extends Omit<BikeBusStop, 'bikeBusId' | 'createdAt' | 'updatedAt'> {
  tempId: string;
}

// Custom TimePicker component
const TimePicker = ({ value, onChange }: { value: string; onChange: (time: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Parse the current time value and convert to 12-hour format
  const parseTime = (timeStr: string) => {
    if (!timeStr) return { hour12: '9', minutes: '00', ampm: 'AM' };
    
    const [h, m] = timeStr.split(':');
    const hour24 = parseInt(h);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    
    return {
      hour12: hour12.toString(),
      minutes: m,
      ampm: ampm
    };
  };
  
  const { hour12, minutes, ampm } = parseTime(value);
  
  // Generate 12-hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  
  // Generate minutes (00, 15, 30, 45)
  const minuteOptions = ['00', '15', '30', '45'];
  
  // AM/PM options
  const ampmOptions = ['AM', 'PM'];
  
  const handleTimeChange = (newHour12: string, newMinutes: string, newAmpm: string) => {
    // Convert 12-hour to 24-hour format
    let hour24 = parseInt(newHour12);
    if (newAmpm === 'AM' && hour24 === 12) {
      hour24 = 0;
    } else if (newAmpm === 'PM' && hour24 !== 12) {
      hour24 += 12;
    }
    
    const timeString = `${hour24.toString().padStart(2, '0')}:${newMinutes}`;
    onChange(timeString);
  };
  
  const formatDisplayTime = (time: string) => {
    if (!time) return "Select time";
    const { hour12, minutes, ampm } = parseTime(time);
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal"
          onClick={() => setIsOpen(true)}
        >
          <Clock className="mr-2 h-4 w-4" />
          {formatDisplayTime(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-4">
          <div className="text-sm font-medium">Select Time</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Hour</Label>
              <Select value={hour12} onValueChange={(h) => handleTimeChange(h, minutes, ampm)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {hourOptions.map((hour) => (
                    <SelectItem key={hour} value={hour}>
                      {hour}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Minutes</Label>
              <Select value={minutes} onValueChange={(m) => handleTimeChange(hour12, m, ampm)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {minuteOptions.map((minute) => (
                    <SelectItem key={minute} value={minute}>
                      {minute}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">AM/PM</Label>
              <Select value={ampm} onValueChange={(ap) => handleTimeChange(hour12, minutes, ap)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ampmOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button 
            onClick={() => setIsOpen(false)} 
            className="w-full" 
            size="sm"
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const BikeBusForm = ({ onSuccess, onCancel }: BikeBusFormProps) => {
  const [leader, setLeader] = useState("");
  const [date, setDate] = useState("");
  const [stops, setStops] = useState<FormStop[]>([
    {
      tempId: "1",
      index: 1,
      siteId: "",
      siteName: "",
      stop: "",
      time: ""
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available sites for stops
  const { data: sites = [] } = useQuery({
    queryKey: ["sites"],
    queryFn: bikeBusService.getSites
  });

  const addStop = () => {
    const newStop: FormStop = {
      tempId: Date.now().toString(),
      index: stops.length + 1,
      siteId: "",
      siteName: "",
      stop: "",
      time: ""
    };
    setStops([...stops, newStop]);
  };

  const removeStop = (tempId: string) => {
    if (stops.length <= 1) {
      toast.error("At least one stop is required");
      return;
    }
    const updatedStops = stops.filter(stop => stop.tempId !== tempId);
    setStops(updatedStops);
  };

  const updateStop = (tempId: string, field: keyof FormStop, value: string) => {
    setStops(stops.map(stop => 
      stop.tempId === tempId ? { ...stop, [field]: value } : stop
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!leader.trim() || !date) {
      toast.error("Please fill in all required fields");
      return;
    }

    const invalidStops = stops.filter(stop => !stop.siteId || !stop.stop.trim() || !stop.time);
    if (invalidStops.length > 0) {
      toast.error("Please complete all stop information");
      return;
    }

    setIsSubmitting(true);
    try {
      const bikeBusData = {
        leader: leader.trim(),
        date,
        stops: stops.map(({ tempId, index, ...stop }) => stop)
      };

      await bikeBusService.createBikeBus(bikeBusData);
      toast.success("Bike Bus created");
      onSuccess();
    } catch (error) {
      console.error("Error creating bike bus:", error);
      toast.error("Failed to create bike bus");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedSiteName = (siteId: string) => {
    const site = sites.find(s => s.id === siteId);
    return site ? `${site.name} (${site.organizationName})` : "";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="leader">Group Leader *</Label>
          <Input
            id="leader"
            value={leader}
            onChange={(e) => setLeader(e.target.value)}
            placeholder="Enter group leader name"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="date">Date *</Label>
          <Input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Stops</h3>
          <Button type="button" onClick={addStop} variant="outline" size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            Add Stop
          </Button>
        </div>

        <div className="space-y-3">
          {stops.map((stop, index) => (
            <Card key={stop.tempId}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    Stop {index + 1}
                  </CardTitle>
                  {stops.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeStop(stop.tempId)}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      Site *
                    </Label>
                    <Select
                      value={stop.siteId}
                      onValueChange={(value) => updateStop(stop.tempId, 'siteId', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id}>
                            {site.name} ({site.organizationName})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`stop-label-${stop.tempId}`}>Stop Label *</Label>
                    <Input
                      id={`stop-label-${stop.tempId}`}
                      value={stop.stop}
                      onChange={(e) => updateStop(stop.tempId, 'stop', e.target.value)}
                      placeholder="e.g. Main Entrance, Parking Lot"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Time *
                    </Label>
                    <TimePicker
                      value={stop.time}
                      onChange={(time) => updateStop(stop.tempId, 'time', time)}
                    />
                  </div>
                </div>
                {stop.siteId && (
                  <div className="text-sm text-muted-foreground">
                    Selected: {getSelectedSiteName(stop.siteId)}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" onClick={onCancel} variant="outline">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create Bike Bus"}
        </Button>
      </div>
    </form>
  );
}; 